/* v3 — cache-bust-1776050862 */
import React from 'react';
import { base44 } from '@/api/base44Client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Sparkles, Bell, Clock, Tag, Send, AlertCircle,
  Plus, ChevronDown, ChevronUp, Wrench, Zap, Flame, Wand2
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════
   SemVer engine
   ─────────────────────────────────────────────────────
   v MAJOR . MINOR . PATCH
     MAJOR  = breaking change / fundamental redesign
     MINOR  = new feature (resets PATCH to 0)
     PATCH  = bug fix / small tweak
   ═══════════════════════════════════════════════════════ */
function parseSemVer(str) {
  const clean = (str || '2.0.7').replace(/^v\s*/i, '');
  const parts = clean.split('.').map(Number);
  return {
    major: parts[0] ?? 2,
    minor: parts[1] ?? 0,
    patch: parts[2] ?? 7,
  };
}

function suggestNextVersion(versionStr, bumpType) {
  const { major, minor, patch } = parseSemVer(versionStr);
  switch (bumpType) {
    case 'patch': return `${major}.${minor}.${patch + 1}`;
    case 'minor': return `${major}.${minor + 1}.0`;
    case 'major': return `${major + 1}.0.0`;
    default:      return `${major}.${minor}.${patch + 1}`;
  }
}

const BUMP_TYPES = [
  {
    key: 'patch',
    label: 'Patch',
    sublabel: 'Bug fix / small tweak',
    Icon: Wrench,
    color: 'text-slate-300',
    border: 'border-slate-600',
    activeBorder: 'border-slate-400',
    activeBg: 'bg-slate-800',
    isSignificant: false,
  },
  {
    key: 'minor',
    label: 'Feature',
    sublabel: 'New capability added',
    Icon: Zap,
    color: 'text-cyan-300',
    border: 'border-slate-600',
    activeBorder: 'border-cyan-500',
    activeBg: 'bg-cyan-950',
    isSignificant: true,
  },
  {
    key: 'major',
    label: 'Major',
    sublabel: 'Architectural redesign',
    Icon: Flame,
    color: 'text-amber-300',
    border: 'border-slate-600',
    activeBorder: 'border-amber-500',
    activeBg: 'bg-amber-950',
    isSignificant: true,
  },
];

/* ═══════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════ */
export default function AppVersionChangelog({ open, onOpenChange, isAdmin }) {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [successMsg, setSuccessMsg] = React.useState(null);
  const [publishingId, setPublishingId] = React.useState(null);

  /* New release form state */
  const [showForm, setShowForm] = React.useState(false);
  const [bumpType, setBumpType] = React.useState('minor');
  const [versionInput, setVersionInput] = React.useState('');
  const [releaseNotes, setReleaseNotes] = React.useState('');
  const [isSignificant, setIsSignificant] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [userHint, setUserHint] = React.useState('');
  const [generating, setGenerating] = React.useState(false);

  /* Derive suggested version whenever bumpType or published versions change */
  const latestPublished = versions.find(v => v.published);
  const suggestedVersion = suggestNextVersion(latestPublished?.version_string, bumpType);

  /* Sync version input when bump type or latest version changes */
  React.useEffect(() => {
    setVersionInput(suggestNextVersion(latestPublished?.version_string, bumpType));
    setIsSignificant(BUMP_TYPES.find(b => b.key === bumpType)?.isSignificant ?? false);
  }, [bumpType, latestPublished?.version_string]);

  const loadVersions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = isAdmin
        ? await base44.entities.AppVersion.list('-release_date')
        : await base44.entities.AppVersion.filter({ published: true }, '-release_date');

      let data = raw;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { data = []; }
      }
      setVersions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load AppVersions:', err);
      setError('Could not load versions.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  React.useEffect(() => {
    if (open) {
      loadVersions();
      setShowForm(false);
      setSuccessMsg(null);
      setError(null);
    }
  }, [open, loadVersions]);

  /* ── AI: generate release notes ── */
  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await base44.functions.invoke('generateVersionNotes', {
        bump_type: bumpType,
        version_string: versionInput.trim(),
        previous_version: latestPublished?.version_string || null,
        previous_notes: latestPublished?.release_notes || null,
        user_hint: userHint.trim() || null,
      });
      if (result?.release_notes) {
        setReleaseNotes(result.release_notes);
      } else {
        setError('AI returned no content. Try adding a hint and generating again.');
      }
    } catch (err) {
      console.error('Generate failed:', err);
      setError(`Generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  /* ── Save draft or publish immediately ── */
  const handleCreate = async (publish) => {
    if (!versionInput.trim()) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const record = await base44.entities.AppVersion.create({
        version_string: versionInput.trim(),
        release_notes: releaseNotes.trim(),
        release_date: new Date().toISOString().split('T')[0],
        is_significant: isSignificant,
        published: publish,
        notification_sent: false,
        notification_sent_at: '',
      });

      if (publish && isSignificant) {
        /* Fire notifications via notifyAppUpdate */
        await base44.functions.invoke('notifyAppUpdate', {
          version_id: record.id,
          version: versionInput.trim(),
          releaseNotes: releaseNotes.trim(),
        });
        setSuccessMsg(`v ${versionInput.trim()} published — notifications sent to all officers.`);
      } else if (publish) {
        setSuccessMsg(`v ${versionInput.trim()} published.`);
      } else {
        setSuccessMsg(`v ${versionInput.trim()} saved as draft.`);
      }

      setShowForm(false);
      setReleaseNotes('');
      setUserHint('');
      await loadVersions();
    } catch (err) {
      console.error('Create failed:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  /* ── Publish an existing draft ── */
  const handlePublish = async (version) => {
    setPublishingId(version.id);
    setSuccessMsg(null);
    setError(null);
    try {
      await base44.entities.AppVersion.update(version.id, { published: true });

      if (version.is_significant) {
        await base44.functions.invoke('notifyAppUpdate', {
          version_id: version.id,
          version: version.version_string,
          releaseNotes: version.release_notes,
        });
        setSuccessMsg(`v ${version.version_string} published — notifications sent to all officers.`);
      } else {
        setSuccessMsg(`v ${version.version_string} published.`);
      }

      await loadVersions();
    } catch (err) {
      console.error('Publish failed:', err);
      setError(`Publish failed: ${err.message}`);
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-slate-700 text-slate-100 flex flex-col p-0"
      >
        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-800">
          <SheetTitle className="text-slate-100 flex items-center justify-between">
            <span className="flex items-center gap-2 text-base">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              COMPASS — What's New
            </span>
            {isAdmin && !showForm && (
              <button
                onClick={() => { setShowForm(true); setSuccessMsg(null); setError(null); }}
                className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors font-normal"
              >
                <Plus className="w-3.5 h-3.5" />
                New release
              </button>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto flex flex-col">

          {/* ── Admin: New Release Form ── */}
          {isAdmin && showForm && (
            <div className="mx-4 mt-4 mb-2 rounded-xl border border-slate-700 bg-slate-800/60 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-200">New Release</span>
                <button
                  onClick={() => { setShowForm(false); setUserHint(''); }}
                  className="text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>

              {/* Bump type selector */}
              <div>
                <div className="text-xs text-slate-500 mb-2">What kind of change is this?</div>
                <div className="grid grid-cols-3 gap-2">
                  {BUMP_TYPES.map(({ key, label, sublabel, Icon, color, border, activeBorder, activeBg }) => (
                    <button
                      key={key}
                      onClick={() => setBumpType(key)}
                      className={`
                        flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border text-center transition-all
                        ${bumpType === key
                          ? `${activeBg} ${activeBorder} border`
                          : `bg-slate-900 ${border} border hover:border-slate-500`}
                      `}
                    >
                      <Icon className={`w-4 h-4 ${bumpType === key ? color : 'text-slate-500'}`} />
                      <span className={`text-xs font-medium ${bumpType === key ? color : 'text-slate-400'}`}>
                        {label}
                      </span>
                      <span className="text-[10px] text-slate-600 leading-tight">{sublabel}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Version number (auto-filled, editable) */}
              <div>
                <div className="text-xs text-slate-500 mb-1.5">
                  Version number
                  <span className="ml-2 text-slate-600">
                    (suggested: <span className="text-slate-400 font-mono">{suggestedVersion}</span>)
                  </span>
                </div>
                <Input
                  value={versionInput}
                  onChange={e => setVersionInput(e.target.value)}
                  placeholder="e.g. 2.1.0"
                  className="font-mono bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm"
                />
              </div>

              {/* SemVer legend — contextual hint */}
              <div className="text-[11px] text-slate-600 leading-snug border-l-2 border-slate-700 pl-2">
                {bumpType === 'patch' && '🔧 Patch — fix something broken. Users may not notice.'}
                {bumpType === 'minor' && '⚡ Feature — something users can now do that they couldn\'t before. Sends notification if Significant.'}
                {bumpType === 'major' && '🔥 Major — a fundamental shift. Always warrants an announcement.'}
              </div>

              {/* What changed — hint for AI */}
              <div>
                <div className="text-xs text-slate-500 mb-1.5">
                  What changed?
                  <span className="ml-1 text-slate-600">(brief summary — AI uses this to write notes)</span>
                </div>
                <Input
                  value={userHint}
                  onChange={e => setUserHint(e.target.value)}
                  placeholder="e.g. fixed kanban sort, added version changelog drawer"
                  className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm"
                />
              </div>

              {/* Release notes + Generate button */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs text-slate-500">Release notes</div>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 disabled:text-slate-600 transition-colors"
                  >
                    <Wand2 className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
                    {generating ? 'Generating…' : '✨ Generate with AI'}
                  </button>
                </div>
                <Textarea
                  value={releaseNotes}
                  onChange={e => setReleaseNotes(e.target.value)}
                  placeholder="Describe what changed, or click ✨ Generate with AI above…"
                  className="bg-slate-900 border-slate-700 text-slate-200 text-sm resize-none h-28"
                />
              </div>

              {/* Significant toggle */}
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <div
                  onClick={() => setIsSignificant(p => !p)}
                  className={`w-8 h-4 rounded-full transition-colors flex items-center ${isSignificant ? 'bg-cyan-600' : 'bg-slate-700'}`}
                >
                  <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${isSignificant ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-200 transition-colors">
                  Significant release — notify all officers by email + in-app
                </span>
              </label>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCreate(false)}
                  disabled={saving || !versionInput.trim()}
                  className="flex-1 bg-transparent border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 text-xs h-8"
                >
                  Save draft
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleCreate(true)}
                  disabled={saving || !versionInput.trim()}
                  className="flex-1 bg-cyan-700 hover:bg-cyan-600 text-white text-xs h-8 gap-1"
                >
                  <Send className="w-3 h-3" />
                  {saving ? 'Publishing…' : isSignificant ? 'Publish & Notify' : 'Publish'}
                </Button>
              </div>
            </div>
          )}

          {/* ── Feedback banners ── */}
          {error && (
            <div className="mx-4 mt-3 flex items-start gap-2 text-xs text-red-400 bg-red-900/20 border border-red-800/50 rounded-md px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          {successMsg && (
            <div className="mx-4 mt-3 flex items-start gap-2 text-xs text-emerald-400 bg-emerald-900/20 border border-emerald-800/50 rounded-md px-3 py-2">
              <Bell className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {successMsg}
            </div>
          )}

          {/* ── Version history list ── */}
          <div className="flex-1 px-5 pb-4 mt-4 space-y-5">
            {loading && (
              <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
                Loading releases…
              </div>
            )}

            {!loading && versions.length === 0 && (
              <div className="text-slate-500 text-sm py-8 text-center">
                No versions published yet.
                {isAdmin && !showForm && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowForm(true)}
                      className="text-cyan-400 hover:text-cyan-300 text-xs underline"
                    >
                      Create the first release
                    </button>
                  </div>
                )}
              </div>
            )}

            {!loading && versions.map((v, i) => {
              const isLatest = i === 0 && v.published;
              const { major, minor, patch } = parseSemVer(v.version_string);

              return (
                <div
                  key={v.id}
                  className={`relative pl-4 border-l-2 pb-2 ${
                    isLatest ? 'border-cyan-500' : 'border-slate-700'
                  }`}
                >
                  {/* Version header row */}
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`font-bold text-sm font-mono ${isLatest ? 'text-cyan-300' : 'text-slate-200'}`}>
                      v {v.version_string}
                    </span>

                    {/* SemVer annotation */}
                    <span className="text-[10px] text-slate-600 font-mono">
                      ({major}=major · {minor}=feature · {patch}=patch)
                    </span>

                    {isLatest && (
                      <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-[10px] px-1.5 py-0">
                        Latest
                      </Badge>
                    )}
                    {v.is_significant && (
                      <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 text-[10px] px-1.5 py-0">
                        <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                        Significant
                      </Badge>
                    )}
                    {isAdmin && !v.published && (
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] px-1.5 py-0">
                        Draft
                      </Badge>
                    )}
                    {v.notification_sent && (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] px-1.5 py-0">
                        <Bell className="w-2.5 h-2.5 mr-0.5" />
                        Notified
                      </Badge>
                    )}
                  </div>

                  {/* Date */}
                  {v.release_date && (
                    <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(v.release_date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                  )}

                  {/* Release notes */}
                  {v.release_notes ? (
                    <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                      {v.release_notes}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600 italic">No release notes.</div>
                  )}

                  {/* Admin: publish draft CTA */}
                  {isAdmin && !v.published && (
                    <Button
                      size="sm"
                      onClick={() => handlePublish(v)}
                      disabled={publishingId === v.id}
                      className="mt-3 bg-cyan-700 hover:bg-cyan-600 text-white text-xs h-7 px-3 gap-1"
                    >
                      <Send className="w-3 h-3" />
                      {publishingId === v.id
                        ? 'Publishing…'
                        : v.is_significant
                        ? 'Publish & Notify All'
                        : 'Publish'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800 flex items-center gap-1.5 text-[11px] text-slate-600">
          <Tag className="w-3 h-3" />
          COMPASS version history · v MAJOR.MINOR.PATCH
        </div>
      </SheetContent>
    </Sheet>
  );
}
